import { TrackType } from '@/player-config/src/types/Track/Type';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { ElementType } from '@/player-config/src/types/Element/Type';
import { CompositionConfig } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { SegmentsTrack } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { Track } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/Track';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { ElementsTrack } from '@/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { VideoElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { ImageElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { AudioElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/AudioElement';
import { Effect } from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Effects';
import { Scene } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { LottieElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';
import { TextElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/TextElement';
import { currentClip, saveClipChanges, updateClipMetadataWithId } from '@/stores/clip';
import { updateClipDataWithSplits } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/updateWithSplits';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import type { SanitizedTrim } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/types';
import { type Clip } from '@/player-config/src/types/clip-types';
import { CaptionStyle, CaptionLineConfig } from '@/player-config/src/types/asset-types';
import { DEFAULT_FONT_SIZE } from '@/player-config/src/constants/player';
import { getCaptionPreset } from '@/player-config/src/caption-presets/utils';
import { getCaptionHighlightColor } from '@/adapters/aiEdits/utils/aiEditsProcessor';
import { mergeDeletes } from '@/player-config/src/utils/assetMetadataUtils';
import { insertElementIntoNewNonOverlappingTrack } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { updatePlayerConfigWithDeletes } from '@/player-config/src/utils/elementUtils';
import { getClipFPS } from '@/player-config/src/utils/clipUtils';
import { computeCaptionsForClip } from '@/App/remotion/libs/caption/caption';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

function mergeTrimsWithExistingDeletes(
  existingDeletes: Record<number, any>,
  trims: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>
): Record<number, any> {
  const newDeletes = trims.reduce(
    (acc, trim) => {
      acc[trim.bounds[0]] = trim;
      return acc;
    },
    { ...existingDeletes }
  );
  return mergeDeletes(newDeletes);
}

/**
 * Adjusts clip start/end boundaries based on trims that overlap with boundaries
 */
function adjustClipBoundariesForTrims(
  originalStart: number,
  originalEnd: number,
  trims: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>
): { adjustedStart: number; adjustedEnd: number } {
  let adjustedStart = originalStart;
  let adjustedEnd = originalEnd;

  trims.forEach(trim => {
    if (trim.bounds[0] <= originalStart && trim.bounds[1] > originalStart) {
      adjustedStart = trim.bounds[1];
    }
    if (trim.bounds[1] >= originalEnd && trim.bounds[0] < originalEnd) {
      adjustedEnd = trim.bounds[0];
    }
  });

  return { adjustedStart, adjustedEnd };
}

function processDeletesAndUpdateClip(
  clipId: string,
  trims: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>
) {
  // NOTE: This getSnapshot() call intentionally reads the clip state that was written by the
  // updateClipMetadataWithId call in Step 1 of updateAllElementsWithAIEdits.
  const updatedClip = currentClip.getSnapshot()[clipId];
  const updatedClipMetadata = updatedClip.asset_metadata;

  const mergedDeletes = mergeTrimsWithExistingDeletes(updatedClipMetadata.deletes || {}, trims);

  const { adjustedStart, adjustedEnd } = adjustClipBoundariesForTrims(
    updatedClipMetadata.start,
    updatedClipMetadata.end,
    trims
  );

  const isBoundaryTrim = adjustedStart !== updatedClipMetadata.start || adjustedEnd !== updatedClipMetadata.end;

  const clipWithDeletesAndBoundaries: Clip = {
    ...updatedClip,
    asset_metadata: {
      ...updatedClipMetadata,
      deletes: mergedDeletes,
      start: adjustedStart,
      end: adjustedEnd
    }
  };

  const captionsData = computeCaptionsForClip(clipWithDeletesAndBoundaries);

  const metadataUpdate: Partial<Clip['asset_metadata']> = {
    deletes: mergedDeletes,
    start: adjustedStart,
    end: adjustedEnd,
    captions: captionsData.captions,
    excludes: captionsData.excludes,
    duration: captionsData.duration
  };

  if (isBoundaryTrim) {
    // For boundary trims, config.ts skips element adjustment (start/end guard fires).
    // Manually adjust elements here using new deletes but same start/end so the
    // updatePlayerConfigWithDeletes guard does not skip adjustment.
    const fps = getClipFPS(updatedClip);
    const updatedPlayerConfig = JSON.parse(JSON.stringify(updatedClipMetadata.player_config)) as CompositionConfig;

    const allElementTracks = updatedPlayerConfig.playerConfig!.tracks.filter(
      track => track.type === TrackType.ELEMENTS
    ) as ElementsTrack[];
    const allNonElementTracks = updatedPlayerConfig.playerConfig!.tracks.filter(
      track => track.type !== TrackType.ELEMENTS
    ) as Track[];

    // Pass same start/end as prevClip so the guard inside updatePlayerConfigWithDeletes
    // does not bail out, ensuring adjustElementsWithDeletes runs exactly once.
    const clipWithNewDeletesOnly: Clip = {
      ...updatedClip,
      asset_metadata: {
        ...updatedClipMetadata,
        deletes: mergedDeletes
      }
    };

    const adjustedElementTracks = updatePlayerConfigWithDeletes({
      playerConfig: allElementTracks,
      fps,
      prevClip: updatedClip,
      newClip: clipWithNewDeletesOnly,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations],
      zoomLevel: zoomLevelStore.getSnapshot().zoomLevel
    });

    updatedPlayerConfig.playerConfig!.tracks = [...allNonElementTracks, ...adjustedElementTracks];
    metadataUpdate.player_config = updatedPlayerConfig;
  }
  // For internal trims (start/end unchanged) we intentionally do NOT save player_config.
  // config.ts will run updatePlayerConfigWithDeletes exactly once via getFinalClipWithRemotionConfig.

  updateClipMetadataWithId(clipId, metadataUpdate);
}

function computeCaptionUpdates(
  clipMetadata: Clip['asset_metadata'],
  transitionsToNonAudiogramLayout: boolean
): Partial<Clip['asset_metadata']> {
  const suitableBrandkitColor = getCaptionHighlightColor();
  const siliconPreset = getCaptionPreset('silicon');

  const customSiliconPreset = {
    ...siliconPreset,
    wordStyle: {
      ...siliconPreset.wordStyle,
      highlightBoxStyle: {
        ...siliconPreset.wordStyle?.highlightBoxStyle,
        background: suitableBrandkitColor
      }
    }
  };

  const isPreviousLayoutAudiogram = clipMetadata.layout === 'AUDIOGRAM';

  const captionStylesUpdate = {
    caption_styles: {
      scale: 1,
      font_size: DEFAULT_FONT_SIZE,
      caps: false,
      line_config: clipMetadata.caption_styles?.line_config || CaptionLineConfig.OneLine,
      animation: clipMetadata.caption_styles?.animation || CaptionStyle.Outline,
      ...clipMetadata.caption_styles,
      ...customSiliconPreset,
      pageStyle: {
        ...clipMetadata.caption_styles?.pageStyle,
        textShadow: '4px 4px 6px rgb(0, 0, 0)'
      }
    },
    caption_positions: {
      x: 2.5,
      y: 82,
      width: 95,
      height: 18
    },
    subtitle: true
  };

  if (transitionsToNonAudiogramLayout) {
    // A non-AUDIOGRAM layout is coming in from composite scenes.
    // Update captions if: first time getting subtitle, OR transitioning away from audiogram.
    if (!clipMetadata.subtitle || isPreviousLayoutAudiogram) {
      return {
        ...captionStylesUpdate,
        layout: isPreviousLayoutAudiogram ? 'SPEAKER' : clipMetadata.layout
      };
    }
    return {};
  }

  // No incoming layout from composite scenes — rely on subtitle only.
  // If current layout is AUDIOGRAM, do nothing (new is audiogram = keep as-is).
  if (!clipMetadata.subtitle && !isPreviousLayoutAudiogram) {
    return { ...captionStylesUpdate };
  }

  return {};
}

export function updateAllElementsWithAIEdits(
  clipId: string,
  elements: (VideoElement | ImageElement | AudioElement | LottieElement | TextElement)[] | undefined,
  effects?: Effect[],
  sceneChanges?: Scene[],
  trims?: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>,
  skipSave?: boolean,
  transitionsToNonAudiogramLayout?: boolean
) {
  const clip = currentClip.getSnapshot()[clipId];
  const clipMetadata = clip.asset_metadata;

  const playerConfig = JSON.parse(JSON.stringify(clip.asset_metadata.player_config)) as CompositionConfig;
  if (!playerConfig) {
    return;
  }

  // Only process elements/tracks if elements is explicitly provided (not undefined)
  if (elements !== undefined) {
    const allExistingTracks = playerConfig?.playerConfig?.tracks || [];
    const allExistingNonElementsTracks = allExistingTracks.filter(
      track => track.type !== TrackType.ELEMENTS
    ) as Track[];
    const allExistingElementTracks = allExistingTracks.filter(
      track => track.type === TrackType.ELEMENTS
    ) as ElementsTrack[];

    const aiEditsElementsById = new Map(elements.map(element => [element.id, element]));

    const filteredElementsTracksWithRetainedElements = allExistingElementTracks
      .map(track => ({
        ...track,
        elements: track.elements
          .filter(
            element =>
              aiEditsElementsById.has(element.id) ||
              ![ElementType.VIDEO, ElementType.TEXT, ElementType.AUDIO].includes(element.type)
          )
          .map(element => aiEditsElementsById.get(element.id) ?? element)
      }))
      .filter(track => track.elements.length > 0);

    const existingElementIds = allExistingElementTracks.flatMap(track => track.elements.map(element => element.id));

    const newElements = elements.filter(element => !existingElementIds.includes(element.id));

    // Distribute new elements into non-overlapping tracks using the existing utility
    let updatedElementTracks = [...filteredElementsTracksWithRetainedElements];

    for (const element of newElements) {
      updatedElementTracks = insertElementIntoNewNonOverlappingTrack(updatedElementTracks, element);
    }

    playerConfig.playerConfig!.tracks = [...allExistingNonElementsTracks, ...updatedElementTracks];
  }

  const compositeSegment = (
    playerConfig.playerConfig.tracks.find(track => track.type === TrackType.SEGMENTS) as SegmentsTrack
  )?.segments.find(segment => segment.type === SegmentType.COMPOSITE) as CompositeSegment;

  if (compositeSegment) {
    if (effects?.length) {
      compositeSegment.effects = effects;
    }
  }

  const updatedCaptionDetails = computeCaptionUpdates(clipMetadata, transitionsToNonAudiogramLayout ?? false);

  // Step 1: First update clip with elements, effects, and scenes
  updateClipMetadataWithId(clipId, {
    ...updatedCaptionDetails,
    magicLayout: {
      ...(clipMetadata.magicLayout as any),
      showSpeakerLabels: false
    },
    player_config: playerConfig as any,
    ...(sceneChanges?.length && { scenes: sceneChanges })
  });

  // Step 2: Process deletes if present
  if (trims && trims.length > 0) {
    processDeletesAndUpdateClip(clipId, trims);
  }

  if (!skipSave) {
    saveClipChanges({ clipId, skipRemotionUpdate: true });
  }
}

export function updateAllElementsWithAIEditsAndSplits(
  clipId: string,
  elements: (VideoElement | ImageElement | AudioElement | LottieElement | TextElement)[] | undefined,
  compositeScenes: Array<{ compositeId: string; scenes: Scene[] }>,
  compositeEffects: Array<{ compositeId: string; effects: Effect[] }>,
  compositeDeletes: Array<{ compositeId: string; trims: SanitizedTrim[] }>
) {
  // Step A: Apply elements + caption preset to player_config

  // Check if any composite scene has a non-AUDIOGRAM layout.
  // This tells updateAllElementsWithAIEdits that the clip is transitioning to a non-audiogram layout.
  const transitionsToNonAudiogramLayout = compositeScenes
    .flatMap(({ scenes }) => scenes)
    .some(scene => scene.details.layout !== 'AUDIOGRAM');

  updateAllElementsWithAIEdits(clipId, elements, [], [], [], true, transitionsToNonAudiogramLayout);

  const clipAfterElements = currentClip.getSnapshot()[clipId];

  // Step B: Set scenes per split (skip post-processing)
  const clipB = updateClipDataWithSplits(
    { clipId, actionType: CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_SCENES, compositeScenes },
    { skipPostProcessing: true, currentClipData: clipAfterElements }
  );

  // Step C: Set effects per split (skip post-processing)
  const clipC = updateClipDataWithSplits(
    { clipId, actionType: CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_EFFECTS, compositeEffects },
    { skipPostProcessing: true, currentClipData: clipB }
  );

  // Step D: Add deletes per split — runs full post-processing + playerConfig rebuild
  // Always called (even with empty deletes) to ensure post-processing runs after Steps B and C

  updateClipDataWithSplits(
    { clipId, actionType: CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_DELETES, compositeDeletes },
    { currentClipData: clipC }
  );
}
